import { useFormContext } from "@mittwald/flow-components/dist/components/Form";
import OrderResourceSelect from "@mittwald/flow-components/dist/components/OrderResourceSelect/OrderResourceSelect";
import { Section } from "@mittwald/flow-components/dist/components/Section";
import SelectBox from "@mittwald/flow-components/dist/components/SelectBox";
import { Text } from "@mittwald/flow-components/dist/components/Text";
import { TextWithIconLayout } from "@mittwald/flow-components/dist/components/TextWithIconLayout";
import { iconInfoCircle } from "@mittwald/flow-icons/dist/infoCircle";
import type { FC } from "react";
import React, { useMemo } from "react";
import { ArticleFactory } from "../../../../../../model/article";
import DedicatedServerArticle from "../../../../../../model/article/DedicatedServerArticle";
import VServerArticle from "../../../../../../model/article/VServerArticle";
import type Server from "../../../../../../model/server/Server";
import ProjectArticleUI from "../../../../../../model/ui/article/ProjectArticleUI";
import SpaceServerArticleUI from "../../../../../../model/ui/article/ServerArticleUI";
import type { AdjustStorageFormFields } from "../../../types";

export interface MachineTypeSectionProps {
  server?: Server;
  article?: VServerArticle | DedicatedServerArticle;
}

export const MachineTypeSection: FC<MachineTypeSectionProps> = (props) => {
  const { server, article } = props;
  const selectedStorageSize =
    useFormContext<AdjustStorageFormFields>().watch("storageSizeInBytes");

  const currentArticle = server?.useArticle();
  const activeArticle = currentArticle ?? article;

  const storageSizeChanged =
    !server || selectedStorageSize !== server.storage.bytes.bytes;

  const vServerArticles: VServerArticle[] =
    ArticleFactory.useLoadAllVServerArticles(VServerArticle.articleTag);

  const dedicatedServerArticles: DedicatedServerArticle[] =
    ArticleFactory.useLoadAllDedicatedServerArticles();

  const promoteArticles = !server;
  const isDedicatedServerType = activeArticle instanceof DedicatedServerArticle;
  const form = useFormContext();
  const resourceFilter = form.watch("resourceFilter");

  const filteredServerArticles = useMemo(() => {
    const baseArticles = isDedicatedServerType
      ? dedicatedServerArticles
      : vServerArticles;

    if (isDedicatedServerType && resourceFilter) {
      return baseArticles.filter((article) => {
        if (article.data.tags && Array.isArray(article.data.tags)) {
          return article.data.tags.some(
            (tag: any) => tag.name === resourceFilter,
          );
        }
        return true;
      });
    }

    return baseArticles;
  }, [
    isDedicatedServerType,
    dedicatedServerArticles,
    vServerArticles,
    resourceFilter,
  ]);

  const options = useMemo(() => {
    if (isDedicatedServerType) {
      return SpaceServerArticleUI.buildDedicatedServerSelectBoxOptions(
        filteredServerArticles as DedicatedServerArticle[],
        promoteArticles,
        activeArticle,
      );
    } else {
      return SpaceServerArticleUI.buildVServerSelectBoxOptions(
        filteredServerArticles,
        promoteArticles,
        activeArticle as VServerArticle,
      );
    }
  }, [
    filteredServerArticles,
    promoteArticles,
    activeArticle,
    isDedicatedServerType,
  ]);

  return (
    <Section.Item headline="machineType">
      {isDedicatedServerType && (
        <>
          <SelectBox
            name="resourceFilter"
            options={ProjectArticleUI.buildResourceSelectBoxOptions(
              currentArticle,
            )}
          />
          <Text i18n={`resourceDescription.${resourceFilter}`} small squished />
        </>
      )}
      <OrderResourceSelect
        minOptionWidth={170}
        name="articleId"
        options={options}
        rules={{
          validate: {
            sameValue: (articleId: string) =>
              storageSizeChanged ||
              activeArticle === undefined ||
              articleId !== activeArticle.id,
          },
        }}
      />
      <TextWithIconLayout icon={iconInfoCircle}>
        <Text
          i18n={{
            id: isDedicatedServerType
              ? "machineTypeAdditionalInfoDedicated"
              : "machineTypeAdditionalInfo",
          }}
          small
          squished
        />
      </TextWithIconLayout>
    </Section.Item>
  );
};

export default MachineTypeSection;
